/**
 * admissionWorkspaceModel — SINGLE SOURCE for the Variant-B workspace's ONE
 * status-driven task bar (UX-1 AC2/AC4). The split-pane replaced the three
 * duplicated action rows (collapsed row + expanded detail + running-bill
 * header) of Variant A with ONE bar; this pure selector is the one place the
 * action set is derived from the admission's STATUS (never the logged-in
 * role — role only gates via the `caps` booleans the component passes in).
 *
 * Keeping the status branching here (not inline in the component) means the
 * taskbar can be unit-tested for every state (admitted / awaiting-bed /
 * discharged-with-dues / discharged-settled / cancelled) and can never drift
 * from a second copy. The `kind` on each action is the SAME `onAction(kind,
 * admission)` contract the desk dialogs already route on — no new dialog.
 */

import {
  ADMISSION_STATUS,
  DUES_STATUS,
  isTerminal,
  cancelBlockedReason,
} from '../../constants/admission';

/**
 * @param {object} admission  the selected admission row (server denorm)
 * @param {object} caps       permission/eligibility booleans resolved by the
 *   component from `usePermission` + status helpers:
 *   { canAddCharge, canTransferBed, canOrderLab, canCollectDeposit,
 *     canGrantConcession, canDischarge, dischargeResume, canCancel,
 *     canCollectDues, canWriteOffDues, canPrintWriteoffVoucher, canAdmitPatient,
 *     moneyHidden }
 *   `canAdmitPatient` (ADMIT_PATIENT) gates Change doctor + Update estimate + Attender details.
 *   `moneyHidden` (admission-access, `isMoneyHidden(admission)`): the server
 *   withheld every money field, so every money action is dropped here — one
 *   place, never a button that would be refused (S4-AC5 / AC-NEG). The Discharge
 *   step, bed actions, lab order, Plan discharge and Update estimate (its dialog
 *   is amount-free for a hidden viewer) are not money actions.
 * @returns {{
 *   primary: Array<{key,label,kind,tone}>,   // buttons on the bar
 *   more: Array<{key,label,kind,tone}>,      // items in the "More" menu
 *   isClosed: boolean,                       // no live action (settled/cancelled)
 *   closedMessage: string|null,
 * }}
 */
export function deriveTaskbar(admission, caps = {}) {
  const a = admission || {};
  const status = a.status;

  // Terminal (DISCHARGED / DECEASED / TRANSFERRED_OUT / CANCELLED) — the ONLY
  // live actions are dues collection/write-off on a discharged-with-dues stay;
  // everything else (incl. a CANCELLED admission, AC-NEG) shows the closed
  // "No actions available" line, never a dangling live taskbar.
  if (isTerminal(status)) {
    const duesOpen = a.dues_status === DUES_STATUS.OPEN && Number(a.dues_paise) > 0;
    const writtenOff = a.dues_status === DUES_STATUS.WRITTEN_OFF;
    const primary = [];
    // collect-dues is RETIRED (O1: POST dues/collect → 410 ROUTE_RETIRED; the
    // collect-dues UI is unmounted). The write-off exception is the only live
    // terminal dues action; a residual is settled at discharge, not after.
    if (caps.canWriteOffDues && duesOpen && !caps.moneyHidden) {
      primary.push({ key: 'writeOffDues', label: 'Write off…', kind: 'writeOffDues', tone: 'writeoff' });
    }
    if (caps.canPrintWriteoffVoucher && writtenOff && !caps.moneyHidden) {
      primary.push({ key: 'printWriteoffVoucher', label: 'Print write-off record', kind: 'printWriteoffVoucher', tone: 'default' });
    }
    return {
      primary,
      more: [],
      isClosed: primary.length === 0,
      closedMessage: 'No actions available — admission closed and settled.',
    };
  }

  const primary = [];
  const noBed = !a.current_bed_id;

  // Add charge — relocated off the Bill-tab header (single home, owner-locked
  // order: Add Charge · Order Lab · Add Deposit) so it's the FIRST action on
  // the bar. Same pair the Bill-tab panel itself gates the button on
  // (CHARGE_PERMISSIONS.ADD_CHARGE + an IP number); terminal statuses never
  // reach this branch (see the early return above), matching the panel's own
  // `!terminal` gate.
  if (caps.canAddCharge && !!a.ip_number && !caps.moneyHidden) {
    primary.push({ key: 'addCharge', label: '+ Add charge', kind: 'addCharge', tone: 'primary' });
  }

  // Awaiting bed → "Assign bed"; bedded (+TRANSFER_BED) → "Change bed" (AC4).
  if (noBed && status === ADMISSION_STATUS.ADMITTED) {
    primary.push({ key: 'assignBed', label: 'Assign bed', kind: 'assignBed', tone: 'default' });
  } else if (!noBed && caps.canTransferBed && status === ADMISSION_STATUS.ADMITTED) {
    primary.push({ key: 'transferBed', label: 'Change bed', kind: 'transferBed', tone: 'default' });
  }
  if (caps.canOrderLab && !!a.ip_number) {
    primary.push({ key: 'orderLab', label: 'Order Diagnostic', kind: 'orderLab', tone: 'default' });
  }
  if (caps.canCollectDeposit && !caps.moneyHidden) {
    primary.push({ key: 'deposit', label: '+ Deposit', kind: 'deposit', tone: 'primary' });
  }
  if (caps.canGrantConcession && !caps.moneyHidden) {
    primary.push({ key: 'concession', label: 'Waiver / concession', kind: 'concession', tone: 'default' });
  }
  // FIX-3 two-step discharge (owner, 2026-08-04): discharge is now a VISIBLE
  // two-step. A still-in-treatment stay shows "Initiate discharge" (step 1 —
  // mints the invoice number, flips Interim Bill → Invoice, bed stays); once
  // DISCHARGE_INITIATED/BILLING_CLEARANCE it shows "Complete discharge" (step 2
  // — settles + frees the bed). Both open the SAME dialog (kind 'discharge'),
  // which is itself status-driven; `dischargeResume` = isInDischargeFlow.
  if (caps.canDischarge) {
    primary.push({
      key: 'discharge',
      label: caps.dischargeResume ? 'Complete discharge' : 'Initiate discharge',
      kind: 'discharge',
      tone: 'primary',
    });
  }

  // "More" — the low-frequency management actions (kept off the primary bar so
  // it stays a 2-second read).
  // Change doctor + Update estimate + Attender details: their server routes
  // (POST /primary-doctor, PUT /plan-estimate, and PATCH /admissions/{id} — whose
  // field-scope guard refuses any non-discharge-date field without ADMIT_PATIENT,
  // so a doctor's attender save is a 403) require ADMIT_PATIENT, so the same slug
  // gates them here (bk-1679) — never a control the server refuses. Update
  // estimate stays offered to a money-hidden ADMIT_PATIENT holder: the dialog
  // drops every amount and its request carries no money lines
  // (EditPlanEstimateDialog).
  const more = [
    ...(caps.canAdmitPatient ? [
      { key: 'changeDoctor', label: 'Change doctor', kind: 'changeDoctor' },
      { key: 'editPlan', label: 'Update estimate', kind: 'editPlan' },
      { key: 'attender', label: 'Attender details', kind: 'attender' },
    ] : []),
    { key: 'patientDetails', label: 'Patient Details', kind: 'patientDetails' },
  ];
  // Cancel initiation — only while the discharge is initiated (caps.canCancelInitiation
  // = DISCHARGE_PATIENT && isInDischargeFlow, resolved by the component).
  if (caps.canCancelInitiation) {
    more.push({ key: 'cancelInitiation', label: 'Cancel initiation…', kind: 'cancelInitiation', tone: 'danger' });
  }
  if (caps.canCancel) {
    // Money guard rail (interim FE-only — see cancelBlockedReason): a held
    // deposit or posted net charges disable the action WITH a visible reason
    // rather than hiding it, so a genuinely mistaken admission still has a
    // discoverable way out once the money is cleared.
    const blockedReason = cancelBlockedReason(a);
    more.push({
      key: 'cancel',
      label: 'Cancel admission',
      kind: 'cancel',
      tone: 'danger',
      disabled: !!blockedReason,
      disabledReason: blockedReason,
    });
  }

  return { primary, more, isClosed: false, closedMessage: null };
}
